import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CATEGORIAS_COMPRA, listPurchases } from '../services/purchasesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function PurchasesPage() {
  const { ruc, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('todos');

  const canManage = user?.rol !== 'prueba';

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listPurchases({
          ruc,
          search,
          categoria,
          cursor: reset ? null : cursor,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, categoria, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search, categoria]); // eslint-disable-line react-hooks/exhaustive-deps

  const catLabel = (id) => CATEGORIAS_COMPRA.find((c) => c.id === id)?.label || id || '—';

  const columns = [
    {
      key: 'serie',
      label: 'Comprobante',
      render: (r) => `${r.serie || '—'}-${r.correlativo ?? '—'}`,
    },
    {
      key: 'proveedor',
      label: 'Proveedor',
      render: (r) => r.proveedorRazonSocial || r.proveedorRuc || '—',
    },
    { key: 'fechaEmision', label: 'Fecha', render: (r) => formatDate(r.fechaEmision || r.createdAt) },
    { key: 'categoria', label: 'Categoría', render: (r) => catLabel(r.categoria) },
    { key: 'total', label: 'Total', render: (r) => formatMoney(r.total) },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => <Badge tone={r.estado === 'registrado' ? 'ok' : 'neutral'}>{r.estado || '—'}</Badge>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <button
          type="button"
          className="btn btn-mini"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/compras/${r.id}`);
          }}
        >
          Ver
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Compras</h1>
          <p className="muted">
            Colección <code>compras</code> · cabecera de comprobante de compra · no duplica stock
          </p>
        </div>
        {canManage ? (
          <Link className="btn btn-primary" to="/admin/compras/nueva">
            Nueva compra
          </Link>
        ) : null}
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar serie, proveedor o RUC…" />
        <select className="select-inline" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          <option value="todos">Todas las categorías</option>
          {CATEGORIAS_COMPRA.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingState label="Cargando compras…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="id"
            onRowClick={(row) => navigate(`/admin/compras/${row.id}`)}
            empty={
              <EmptyState
                title="No hay compras"
                description="Registra comprobantes de compra o usa el OCR desde la app móvil."
              />
            }
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}
    </div>
  );
}
